import React, {useEffect, useCallback, memo, useRef, useLayoutEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {H3, P} from '../Atoms/Typography'
import {useNavigation, useCurrentRoute} from 'react-navi'
import {Baseline, Row, InvisibleContainer, TitleRow} from '../Layout/Grid'
import {useAppState} from '../../state'
import {MainLayoutWithoutStretch, MainContent} from '../Layout/Layout'
import {A11yAccordion} from '../Atoms/Accordion'
import {HeaderRow, GoBackHeaderTitle, HeaderActions} from '../Molecules/ViewComponents/ViewHeader'
import {Loader} from '../../Routes'
import {EmptyTaskList} from './Home/NoContent'
import {A11yListContainer, A11yIconListItem} from '../Atoms/Lists'
import i18n from 'i18next'
import {Helmet} from 'react-navi-helmet-async'
import {taskGroupNameArgs, normalizeLocations} from '../../config/utils'
import {sortByName} from '../../config/utils'
import {ErrorMessage} from '../Molecules/Errors/ErrorMessage'
import {TaskSettings} from '../../state/settings/tasks/state'
import {taskGroupIconPicker} from '../../pages/tasks/utils'
import {SharedIcon} from '../../sharedComponents/components'
import styled from 'styled-components'
import {Dropdown} from '../../sharedComponents/components'
import {Checkbox as SharedCheckbox} from '../../sharedComponents/components/molecules/Checkbox/Checkbox'

// (SiteName | SharedTasks) -> TaskGroups
interface SettingsBlockProps {
  blockName: string
  blockData: SettingsSubBlockProps
  openByDefault?: boolean
  // function to register per-task refs for scrolling by id
  registerRef?: (id: string, el: HTMLDivElement | null) => void
  recentTaskId?: string | undefined
  // selected task names (from filters) so groups containing selected tasks can auto-open
  selectedTaskNames?: string[]
  tasksBySite?: TaskSettings
  taskSiteNamesById?: {[key: string]: string[]}
}

// TaskGroup -> Tasks
interface SettingsSubBlockProps {
  [subBlockName: string]: SettingsRowProps[]
}

interface SettingsRowProps {
  id: string
  name: string
  icon: string
  siteExamples: string[]
  siteAmount: number
  target: string
  type: string
  siteName?: string
  siteNamesFull?: string[]
}

// Tasksettings siteExamples is a subset of all sites related to a task
const SITE_EXAMPLES_AMOUNT_MAX = 2

const EditTasks = memo(() => {
  const {state, actions} = useAppState()
  const nav = useNavigation()
  const route = useCurrentRoute()
  const {t} = useTranslation(['tasks', 'settings'])

  type MultiFilters = {
    locations: string[]
    sites: string[]
    taskGroups: string[]
    taskNames: string[]
  }

  // Initialize from sessionStorage (migrate old single-value shape if present)
  const [filters, setFilters] = useState<MultiFilters>(() => {
    try {
      const preserve = sessionStorage.getItem('preserveTaskFilters') === '1'
      if (preserve) {
        const raw = sessionStorage.getItem('taskFilters')
        try {
          sessionStorage.removeItem('preserveTaskFilters')
        } catch (err) {
          console.error(err)
        }
        if (raw) {
          try {
            const parsed = JSON.parse(raw)
            // migrate old single-value fields to arrays when necessary
            return {
              locations: parsed.locations || (parsed.location ? [parsed.location] : []),
              sites: parsed.sites || (parsed.site ? [parsed.site] : []),
              taskGroups: parsed.taskGroups || (parsed.taskGroup ? [parsed.taskGroup] : []),
              taskNames: parsed.taskNames || (parsed.taskName ? [parsed.taskName] : [])
            }
          } catch (e) {
            void e
          }
        }
      }
    } catch (e) {
      void e
    }
    return {locations: [], sites: [], taskGroups: [], taskNames: []}
  })
  // now keyed by task id
  const siteRefs = useRef<{[key: string]: HTMLDivElement | null}>({})
  // store the recently edited task id so scrolling can use a stable reference
  const recentlyEditedIdRef = useRef<string | undefined>(undefined)

  // transient flag used to force all accordions closed when Reset is clicked
  const [collapseFlag, setCollapseFlag] = useState(false)
  const collapseTimerRef = useRef<number | null>(null)

  const {data, pending: isLoading, error} = state.v1.settings.tasks
  const {sharedTasks, ...tasksBySite} = data

  // derive locations from the current chain so location filter can map to sites
  const chain = state.chainsById[state.selectedChainId as keyof typeof state.chainsById]
  const locationOptions = chain ? normalizeLocations(chain) : []

  // Helper that performs in-memory filtering of the dataset according to filters.
  const filterTasks = (sTasks: any, tBySite: any, f: MultiFilters) => {
    const filteredBySite: {[site: string]: any} = {}

    const siteSet = new Set(f.sites || [])
    const locationSet = new Set(f.locations || [])
    const groupSet = new Set(f.taskGroups || [])
    const nameSet = new Set(f.taskNames || [])

    // compute allowed sites from selected locations (if any)
    const allowedFromLocations = (() => {
      if (!locationSet.size) return null
      const out = new Set<string>()
      locationOptions.forEach((loc: any) => {
        if (locationSet.has(loc.name) && Array.isArray(loc.sites)) loc.sites.forEach((s: any) => out.add(s.name))
      })
      return out
    })()

    const sitePass = (siteName: string) => {
      if (siteSet.size) return siteSet.has(siteName)
      if (allowedFromLocations) return allowedFromLocations.has(siteName)
      return true
    }

    Object.entries(tBySite || {}).forEach(([siteName, groups]: [string, any]) => {
      if (!sitePass(siteName)) return
      const outGroups: {[k: string]: any[]} = {}
      ;(Object.entries(groups || {}) as [string, any[]][]).forEach(([groupName, tasks]) => {
        if (groupSet.size && !groupSet.has(groupName)) return
        const filteredTasks = (tasks || []).filter((task: any) => {
          if (nameSet.size && (!task.name || !nameSet.has(task.name))) return false
          return true
        })
        if (filteredTasks.length) outGroups[groupName] = filteredTasks
      })
      if (Object.keys(outGroups).length) filteredBySite[siteName] = outGroups
    })

    // Filter shared tasks: keep groups, and tasks that match filters. For site/taskGroup/taskName semantics
    const filteredShared: {[k: string]: any[]} = {}
    ;(Object.entries(sTasks || {}) as [string, any[]][]).forEach(([groupName, tasks]) => {
      if (groupSet.size && !groupSet.has(groupName)) return
      const filtered = (tasks || []).filter((task: any) => {
        if (siteSet.size) {
          if (!task.siteExamples || !(task.siteExamples || []).some((se: string) => siteSet.has(se))) return false
        }
        if (allowedFromLocations) {
          if (!task.siteExamples || !task.siteExamples.some((se: string) => allowedFromLocations.has(se))) return false
        }
        if (nameSet.size && (!task.name || !nameSet.has(task.name))) return false
        return true
      })
      if (filtered.length) filteredShared[groupName] = filtered
    })

    return {filteredSharedTasks: filteredShared, filteredTasksBySite: filteredBySite}
  }

  // Memoize filtered results so we only recompute when data or filters change
  const {filteredSharedTasks, filteredTasksBySite} = React.useMemo(
    () => filterTasks(sharedTasks, tasksBySite, filters),
    [sharedTasks, tasksBySite, JSON.stringify(filters)]
  )

  // Persist filters so they survive navigation away/from this page
  useEffect(() => {
    try {
      sessionStorage.setItem('taskFilters', JSON.stringify(filters))
    } catch (e) {
      console.error(e)
    }
  }, [filters])

  const setup = useCallback(async () => {
    actions.v1.settings.tasks.getTaskSettings()
  }, [actions.v1.settings])

  useEffect(() => {
    setup()
  }, [setup])

  // Keep a stored copy of the id from navigation state so we can use it reliably
  useEffect(() => {
    const idFromRoute = route.state?.id as string | undefined
    const idFromStorage = (() => {
      try {
        return sessionStorage.getItem('recentEditedTaskId') || undefined
      } catch (e) {
        console.error(e)
        return undefined
      }
    })()
    const id = idFromRoute ?? idFromStorage
    if (id) {
      recentlyEditedIdRef.current = id
    }
  }, [route.state?.id])

  const handleCollapseAll = useCallback(() => {
    // clear any stored recent edited id so blocks won't auto-open
    try {
      sessionStorage.removeItem('recentEditedTaskId')
    } catch (e) {
      console.error(e)
    }
    recentlyEditedIdRef.current = undefined

    setCollapseFlag(true)
    if (collapseTimerRef.current) {
      window.clearTimeout(collapseTimerRef.current)
    }

    collapseTimerRef.current = window.setTimeout(() => {
      setCollapseFlag(false)
      collapseTimerRef.current = null
    }, 250)
  }, [])

  // stable registerRef so we can log when rows register themselves
  const registerRef = useCallback((id: string, el: HTMLDivElement | null) => {
    siteRefs.current[id] = el
  }, [])

  // This effect handles scrolling. It runs after the DOM is painted.
  useLayoutEffect(() => {
    // Prefer the stored id, fall back to route.state if needed
    const recentlyEditedId = recentlyEditedIdRef.current ?? (route.state?.id as string | undefined)

    if (!recentlyEditedId) return

    let cancelled = false
    let attempts = 0
    const maxAttempts = 12
    let timeoutId: number | undefined

    const tryScroll = () => {
      if (cancelled) return
      attempts += 1
      const targetElement = siteRefs.current[recentlyEditedId]

      if (targetElement) {
        // Jump immediately to the edited task (no smooth scrolling) and focus it
        targetElement.scrollIntoView({behavior: 'auto', block: 'center'})
        try {
          sessionStorage.removeItem('recentEditedTaskId')
        } catch (e) {
          console.error(e)
        }
        // Focus the element so keyboard users land on it — the element is made focusable in SettingsRow
        try {
          ;(targetElement as HTMLElement).focus()
        } catch (e) {
          console.error(e)
        }
        recentlyEditedIdRef.current = undefined
        return
      }

      if (attempts < maxAttempts && !isLoading) {
        // retry after a short delay — gives nested accordions time to render their children
        timeoutId = window.setTimeout(tryScroll, 150)
      } else {
        return undefined
      }
    }

    // start attempts
    tryScroll()

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isLoading, route.state?.id]) // Rerun when loading is finished or route state changes.

  const getSitesSortedByName = (tasksBySite: TaskSettings) => {
    return Object.entries(tasksBySite).sort(([a, _], [b, __]) => (a > b ? 1 : -1))
  }

  const taskSiteNamesById = React.useMemo(() => {
    const siteNamesById = new Map<string, Set<string>>()

    Object.entries(tasksBySite || {}).forEach(([siteName, groups]: [string, any]) => {
      Object.values(groups || {}).forEach((tasks: any) => {
        ;(tasks || []).forEach((task: any) => {
          let siteNames = siteNamesById.get(task.id)
          if (!siteNames) {
            siteNames = new Set<string>()
            siteNamesById.set(task.id, siteNames)
          }
          siteNames.add(siteName)
        })
      })
    })

    return Array.from(siteNamesById.entries()).reduce((acc, [taskId, siteNames]) => {
      acc[taskId] = Array.from(siteNames).sort((a, b) => a.localeCompare(b))
      return acc
    }, {} as {[key: string]: string[]})
  }, [tasksBySite])

  // Helper to find where a task id lives: returns { siteName, groupName } or undefined
  const findTaskLocation = (id?: string) => {
    if (!id) return undefined
    // search sharedTasks
    if (sharedTasks) {
      for (const [groupName, tasks] of Object.entries(sharedTasks)) {
        if (tasks.some(task => task.id === id)) return {siteName: 'sharedTasks', groupName}
      }
    }
    // search per-site tasks
    for (const [siteName, groups] of Object.entries(tasksBySite)) {
      for (const [groupName, tasks] of Object.entries(groups)) {
        if (tasks.some(task => task.id === id)) return {siteName, groupName}
      }
    }
    return undefined
  }

  const recentlyEditedSite = route.state?.siteName as string | undefined

  // Determine where the recently edited task is located (if we have an id)
  const recentTaskId = recentlyEditedIdRef.current
  const recentLocation = findTaskLocation(recentTaskId)

  // Compute whether shared block should open when filters are applied
  const sharedBlockShouldOpen =
    !collapseFlag &&
    (recentLocation?.siteName === 'sharedTasks' ||
      (Array.isArray(filters.taskGroups) &&
        filters.taskGroups.length > 0 &&
        Object.keys(filteredSharedTasks || {}).some(k => filters.taskGroups.includes(k))) ||
      (Array.isArray(filters.taskNames) &&
        filters.taskNames.length > 0 &&
        Object.values(filteredSharedTasks || {}).some((tasks: any[]) =>
          (tasks || []).some((task: any) => filters.taskNames.includes(task.name))
        )))

  return (
    <MainLayoutWithoutStretch>
      <Helmet title={t('common:routes.taskSettings', 'Task settings')} />
      <Loader show={isLoading} />
      {!isLoading && (
        <MainContent variant="white">
          <Baseline>
            <HeaderRow
              dataCy="settings-edit-tasks-list"
              description={
                <Trans
                  i18nKey="tasks:messages.createEditTasks"
                  defaults="Create new tasks and edit details of existing tasks. In the list, shared tasks displays the tasks which are used in more than one site. View the complete list of a site by selecting the site on the list. To edit task groups and task order, select the setting icon above."
                  components={{br: <br />}}
                />
              }
            >
              <GoBackHeaderTitle
                label={t('tasks:labels.taskSettings', 'Tasks settings')}
                path={'/settings'}
                backLabel={t('settings:labels.settingsTitle', 'Settings')}
              />
              <HeaderActions
                onConfigure={() => nav.navigate('/edit/taskGroups')}
                onAdd={() => nav.navigate('/create/task')}
                onPrint={() => actions.v1.settings.tasks.exportTaskSettings(filters)}
                onPrintLabel={t('tasks:actions.exportTaskList', 'Task list')}
              />
            </HeaderRow>
            <TitleRow>
              <div>
                <Filters
                  tasksBySite={tasksBySite}
                  sharedTasks={sharedTasks}
                  locationOptions={locationOptions}
                  value={filters}
                  onChange={(f: Partial<MultiFilters>) => {
                    setFilters(prev => ({
                      locations: f.locations ?? prev.locations,
                      sites: f.sites ?? prev.sites,
                      taskGroups: f.taskGroups ?? prev.taskGroups,
                      taskNames: f.taskNames ?? prev.taskNames
                    }))
                  }}
                  onCollapse={handleCollapseAll}
                />
              </div>
            </TitleRow>
            {error === null ? (
              <A11yListContainer>
                {!!filteredSharedTasks && Object.keys(filteredSharedTasks).length > 0 && (
                  <SettingsBlock
                    key={`sharedTasks-${collapseFlag ? 'collapsed' : 'normal'}-tg-${(filters.taskGroups || []).join(
                      '|'
                    )}-tn-${(filters.taskNames || []).join('|')}`}
                    blockName={t('tasks:labels.sharedTasks', 'Shared tasks')}
                    blockData={filteredSharedTasks}
                    openByDefault={sharedBlockShouldOpen}
                    registerRef={registerRef}
                    recentTaskId={recentTaskId}
                    selectedTaskNames={filters.taskNames}
                    tasksBySite={tasksBySite}
                    taskSiteNamesById={taskSiteNamesById}
                  />
                )}
                {!Object.keys(tasksBySite).length ? (
                  <EmptyTaskList />
                ) : Object.keys(filteredTasksBySite).length ? (
                  getSitesSortedByName(filteredTasksBySite).map(([siteName, siteTasks]) => {
                    const isOpen =
                      siteName === (route.state?.siteName as string | undefined) ||
                      siteName === recentLocation?.siteName
                    const siteBlockShouldOpen =
                      !collapseFlag &&
                      (isOpen ||
                        (Array.isArray(filters.taskGroups) &&
                          filters.taskGroups.length > 0 &&
                          Object.keys(siteTasks || {}).some(k => filters.taskGroups.includes(k))) ||
                        (Array.isArray(filters.taskNames) &&
                          filters.taskNames.length > 0 &&
                          Object.values(siteTasks || {}).some((tasks: any[]) =>
                            (tasks || []).some((task: any) => filters.taskNames.includes(task.name))
                          )))
                    return (
                      <InvisibleContainer
                        key={`${siteName}-${collapseFlag ? 'collapsed' : 'normal'}-tg-${(filters.taskGroups || []).join(
                          '|'
                        )}-tn-${(filters.taskNames || []).join('|')}`}
                      >
                        <SettingsBlock
                          blockName={siteName}
                          blockData={siteTasks}
                          openByDefault={siteBlockShouldOpen}
                          registerRef={registerRef}
                          recentTaskId={recentTaskId}
                          selectedTaskNames={filters.taskNames}
                          tasksBySite={tasksBySite}
                          taskSiteNamesById={taskSiteNamesById}
                        />
                      </InvisibleContainer>
                    )
                  })
                ) : (
                  // No results after applying filters
                  <NoResults>{t('common:labels.noResults', 'No results found')}</NoResults>
                )}
              </A11yListContainer>
            ) : (
              <ErrorMessage message={'We could not fetch the settings'} />
            )}
          </Baseline>
        </MainContent>
      )}
    </MainLayoutWithoutStretch>
  )
})

const SettingsBlock = ({
  blockName,
  blockData,
  openByDefault = false,
  registerRef,
  recentTaskId,
  selectedTaskNames,
  tasksBySite,
  taskSiteNamesById
}: SettingsBlockProps) => {
  const {t} = useTranslation(['tasks', 'settings'])

  const getTaskGroupsSortedByTranslatedName = (blockData: SettingsSubBlockProps) => {
    return Object.keys(blockData).sort((a, b) => (t(...taskGroupNameArgs(a)) > t(...taskGroupNameArgs(b)) ? 1 : -1))
  }

  return (
    <A11yAccordion offset={2} title={blockName} openByDefault={openByDefault}>
      {getTaskGroupsSortedByTranslatedName(blockData).map(taskGroup => {
        return (
          <A11yAccordion
            offset={4}
            disableSelectedBorder
            title={t(...taskGroupNameArgs(taskGroup))}
            key={taskGroup}
            // open the group if it contains the recently edited task or if a selected task name is in the group
            openByDefault={
              // open if recently edited is inside
              (!!recentTaskId && blockData[taskGroup].some(task => task.id === recentTaskId)) ||
              // or if any selected task name filter corresponds to a task in this group
              (Array.isArray(selectedTaskNames) &&
                selectedTaskNames.length > 0 &&
                blockData[taskGroup].some((task: any) => selectedTaskNames.includes(task.name)))
            }
          >
            {blockData[taskGroup]
              .slice()
              .sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', undefined, {numeric: true, sensitivity: 'base'})
              )
              .map(task => {
                const fullSiteNames = taskSiteNamesById?.[task.id] || task.siteExamples || []

                return (
                  <SettingsRow
                    key={task.id}
                    id={task.id}
                    name={task.name}
                    icon={task.icon}
                    siteExamples={task.siteExamples}
                    siteAmount={task.siteAmount}
                    type={task.type}
                    target={task.target}
                    siteName={blockName}
                    registerRef={registerRef}
                    siteNamesFull={fullSiteNames}
                  />
                )
              })}
          </A11yAccordion>
        )
      })}
    </A11yAccordion>
  )
}

const SettingsRow = (task: SettingsRowProps & {registerRef?: (id: string, el: HTMLDivElement | null) => void}) => {
  const nav = useNavigation()
  const handleClick = (id: string, taskType: string) => {
    // persist id to sessionStorage so it can be used for scrolling when returning
    try {
      // mark that we want to preserve the current filters when navigating to edit
      sessionStorage.setItem('preserveTaskFilters', '1')
    } catch (e) {
      console.error(e)
    }
    try {
      sessionStorage.setItem('recentEditedTaskId', id)
    } catch (e) {
      console.error(e)
    }
    // Pass the task id to the edit page via navigation state so we can scroll back to it.
    nav.navigate(`/edit/task/${taskType}/${id}`, {state: {id}})
  }

  return (
    <div ref={el => task.registerRef?.(task.id, el)} tabIndex={-1}>
      <A11yIconListItem
        offset={5}
        key={task.id}
        icon={<SharedIcon icon={taskGroupIconPicker(task.icon)} />}
        onClick={() => {
          handleClick(task.id, task.type)
        }}
        content={
          <Row justify="space-between">
            <H3>
              {task.name} {task.target ? ' - ' + task.target : null}
            </H3>
            {task.siteExamples.length > 1 && (
              <P
                variant="small"
                title={(Array.isArray(task.siteNamesFull) && task.siteNamesFull.length
                  ? task.siteNamesFull
                  : task.siteExamples || []
                ).join(', ')}
              >
                {getSiteNames(task.siteExamples, task.siteAmount)}
              </P>
            )}
          </Row>
        }
      />
    </div>
  )
}

const getSiteNames = (sites: string[], amount: number) => {
  if (amount <= SITE_EXAMPLES_AMOUNT_MAX) {
    return sites.join(', ')
  } else if (amount > SITE_EXAMPLES_AMOUNT_MAX) {
    return `${sites[0]}, ${amount - 1} ${i18n.t('common:labels.othersAmount', 'others')}`
  }
  return i18n.t('tasks:labels.noSites', 'No sites')
}

// Add a small filters UI that sits between the header description and the task list
const FiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  margin: 1% 10% 8px 0;
  flex-wrap: wrap;
  flex: 1 1 0;
`

const FiltersWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
`

const FilterMenu = styled.div`
  min-width: 160px; /* reduced so menu boxes are smaller */
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.11);
  word-break: break-word;
  box-sizing: border-box;
  width: 100%;
  padding: 2%;
`

const FilterMenuItem = styled.div<{selected?: boolean}>`
  padding: 8px 12px;
  cursor: pointer;
  background: ${p => (p.selected ? '#eef4ff' : 'transparent')};
  font-weight: ${p => (p.selected ? 600 : 400)};
  color: ${p => (p.selected ? '#0b3b91' : '#222')};
  &:hover {
    background: #f5f7fa;
  }
  white-space: normal; /* allow wrapping inside the menu */
  word-break: break-word;
  box-sizing: border-box;
  width: 100%;
`

const FilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 160px;
  min-width: 160px;
`

const FilterLabel = styled.div`
  font-size: 12px;
  color: #666;
  padding-left: 2px;
`

const SearchInput = styled.input`
  display: block;
  width: calc(100% - 16px);
  margin: 8px;
  padding: 6px 8px;
  border: 1px solid #e2e6ea;
  border-radius: 4px;
  font-size: 13px;
`

const FilterButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 10px;
  padding-right: 34px; /* space for caret */

  width: 100%; /* 🔑 fill the FilterColumn */
  min-width: 0; /* allow shrinking inside flex */
  max-width: none; /* remove hard cap */

  border: 1px solid rgb(188, 187, 187);
  border-radius: 6px;
  background: #fff;
  text-align: left;
  font-family: FavoritStd-Book;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;

  &::after {
    content: '▾';
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 12px;
  }

  span {
    flex: 1; /* 🔑 allows shrinking inside button */
    min-width: 0; /* 🔑 required for ellipsis under flexbox */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ResetContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
  align-self: center;
  margin: 1% 0 0 0;
`

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  min-width: 72px;
  height: 36px;
  border: 1px solid rgb(188, 187, 187);
  border-radius: 6px;
  background: #fff;
  color: #0b3b91;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #eef4ff;
  }
`

const NoResults = styled.div`
  padding: 36px 0;
  text-align: center;
  color: #666;
`

const Filters = ({
  tasksBySite,
  sharedTasks,
  locationOptions,
  onChange,
  onCollapse,
  value
}: {
  tasksBySite: any
  sharedTasks: any
  locationOptions?: any[]
  onChange?: (
    _: Partial<{
      locations: string[]
      sites: string[]
      taskGroups: string[]
      taskNames: string[]
    }>
  ) => void
  onCollapse?: () => void
  value?: {locations?: string[]; sites?: string[]; taskGroups?: string[]; taskNames?: string[]}
}) => {
  const {t} = useTranslation(['tasks', 'settings', 'common'])

  // controlled selections come from `value` (single source of truth)
  const selectedLocations = value?.locations || []
  const selectedSites = value?.sites || []
  const selectedTaskGroups = value?.taskGroups || []
  const selectedTaskNames = value?.taskNames || []

  // search queries for each dropdown (local only)
  const [locationQuery, setLocationQuery] = useState('')
  const [siteQuery, setSiteQuery] = useState('')
  const [groupQuery, setGroupQuery] = useState('')
  const [nameQuery, setNameQuery] = useState('')

  // derive options from data and provided locationOptions
  const locations = (locationOptions || []).map(l => l.name)

  // derive full sites and limit by selected location
  const allSites: string[] = Object.keys(tasksBySite || {})
  const allowedSiteNames: string[] = (() => {
    if (!selectedLocations.length || !locationOptions) return allSites
    const out: string[] = []
    selectedLocations.forEach(locName => {
      const loc = (locationOptions as any[]).find((l: any) => l.name === locName)
      if (loc && Array.isArray(loc.sites)) out.push(...loc.sites.map((s: any) => s.name))
    })
    return Array.from(new Set(out))
  })()
  const sites: string[] = selectedSites.length ? selectedSites : allowedSiteNames

  // if a location is selected and the currently selected site is not in that location, clear the site selection in parent
  useEffect(() => {
    if (onChange && selectedSites.length && !sites.includes(selectedSites[0])) {
      onChange({sites: [], taskGroups: [], taskNames: []})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocations, selectedSites, sites])

  // Determine available task groups based on currently visible sites (sites limited by location)
  const groupsFromSites = new Set<string>()
  sites.forEach((siteName: string) => {
    const groups = ((tasksBySite || {}) as {[k: string]: any})[siteName] as {[k: string]: any} | undefined
    if (groups) Object.keys(groups).forEach((g: string) => groupsFromSites.add(g))
  })

  // Include shared task groups only if any of their tasks are used in the visible sites
  const groupsFromShared = new Set<string>()
  if (sharedTasks) {
    ;(Object.entries(sharedTasks) as [string, any[]][]).forEach(([g, tasks]) => {
      if (!sites.length) {
        groupsFromShared.add(g)
      } else if ((tasks || []).some((t: any) => (t.siteExamples || []).some((se: string) => sites.includes(se)))) {
        groupsFromShared.add(g)
      }
    })
  }

  const groupSet = new Set<string>([...Array.from(groupsFromSites), ...Array.from(groupsFromShared)])
  const taskGroups = Array.from(groupSet)
    .slice()
    .sort((a, b) =>
      t(...taskGroupNameArgs(a)).localeCompare(t(...taskGroupNameArgs(b)), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    )

  // Build task names constrained by the selected group (if any) and visible sites
  const nameSet = new Set<string>()
  if (selectedTaskGroups.length) {
    // collect tasks for this group from visible sites
    selectedTaskGroups.forEach((tg: string) => {
      sites.forEach((siteName: string) => {
        const groups = ((tasksBySite || {}) as {[k: string]: any})[siteName] as {[k: string]: any} | undefined
        const tasks = (groups && (groups[tg] as any[])) || []
        ;(tasks as any[]).forEach(t => nameSet.add(t.name))
      })
      const sharedForGroup: any[] = sharedTasks && tg ? ((sharedTasks as any)[tg] as any[]) || [] : []
      ;(sharedForGroup || []).forEach((t: any) => {
        if ((t.siteExamples || []).some((se: string) => sites.includes(se))) nameSet.add(t.name)
      })
    })
  } else {
    // no group selected -> show all task names present in visible sites (including shared tasks used by them)
    sites.forEach((siteName: string) => {
      const groups = ((tasksBySite || {}) as {[k: string]: any})[siteName] as {[k: string]: any} | undefined
      if (groups)
        (Object.values(groups) as any[]).forEach((tasks: any[]) =>
          (tasks || []).forEach((t: any) => nameSet.add(t.name))
        )
    })
    if (sharedTasks) {
      ;(Object.values(sharedTasks) as any[]).forEach((tasks: any[]) =>
        (tasks || []).forEach((t: any) => {
          if ((t.siteExamples || []).some((se: string) => sites.includes(se))) nameSet.add(t.name)
        })
      )
    }
  }
  const taskNames = Array.from(nameSet)
    .slice()
    .sort((a, b) => a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'}))

  // remove invalid selected taskGroups and taskNames when options change
  useEffect(() => {
    if (!onChange) return
    const invalidGroups = selectedTaskGroups.filter(g => !taskGroups.includes(g))
    if (invalidGroups.length) onChange({taskGroups: selectedTaskGroups.filter(g => taskGroups.includes(g))})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(taskGroups), JSON.stringify(selectedTaskGroups)])
  useEffect(() => {
    if (!onChange) return
    const invalidNames = selectedTaskNames.filter(n => !taskNames.includes(n))
    if (invalidNames.length) onChange({taskNames: selectedTaskNames.filter(n => taskNames.includes(n))})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(taskNames), JSON.stringify(selectedTaskNames)])

  // filtered option lists based on search queries (case-insensitive)
  const filteredLocations = locations.filter(l => l.toLowerCase().includes(locationQuery.trim().toLowerCase()))
  const filteredSites = allowedSiteNames.filter((s: string) => s.toLowerCase().includes(siteQuery.trim().toLowerCase()))
  const filteredGroups = taskGroups.filter(g =>
    t(...taskGroupNameArgs(g))
      .toLowerCase()
      .includes(groupQuery.trim().toLowerCase())
  )
  const filteredNames = taskNames.filter(n => n.toLowerCase().includes(nameQuery.trim().toLowerCase()))

  return (
    <FiltersWrapper>
      <FiltersContainer>
        <FilterColumn>
          <FilterLabel>{t('settings:labels.location', 'Location')}</FilterLabel>
          <Dropdown
            style={{width: '100%'}}
            overlay={
              <FilterMenu onClick={e => e.stopPropagation()}>
                <SearchInput
                  placeholder={t('common:placeholders.searchLocations', 'Search location')}
                  value={locationQuery}
                  onChange={e => setLocationQuery(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  data-cy="search-location"
                />
                <FilterMenuItem
                  selected={selectedLocations.length === 0}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onChange?.({locations: [], sites: [], taskGroups: [], taskNames: []})
                    setLocationQuery('')
                  }}
                >
                  <SharedCheckbox
                    id={`filter-location-all`}
                    checked={selectedLocations.length === 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation()
                      onChange?.({locations: [], sites: [], taskGroups: [], taskNames: []})
                      setLocationQuery('')
                    }}
                    label={t('common:labels.taskGroupFilterAll', 'All')}
                  />
                </FilterMenuItem>
                {filteredLocations.map(loc => (
                  <FilterMenuItem
                    key={loc}
                    selected={selectedLocations.includes(loc)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      const next = selectedLocations.includes(loc)
                        ? selectedLocations.filter(s => s !== loc)
                        : [...selectedLocations, loc]
                      onChange?.({locations: next})
                      setLocationQuery('')
                    }}
                  >
                    <SharedCheckbox
                      id={`filter-location-${loc}`.replace(/\s+/g, '-')}
                      checked={selectedLocations.includes(loc)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.stopPropagation()
                        const next = selectedLocations.includes(loc)
                          ? selectedLocations.filter(s => s !== loc)
                          : [...selectedLocations, loc]
                        onChange?.({locations: next})
                        setLocationQuery('')
                      }}
                      label={loc}
                    />
                  </FilterMenuItem>
                ))}
              </FilterMenu>
            }
          >
            <FilterButton data-cy="filter-location">
              <span>
                {selectedLocations.length === 0
                  ? t('common:labels.taskGroupFilterAll', 'All')
                  : selectedLocations.length === 1
                  ? selectedLocations[0]
                  : `${selectedLocations.length} ${t('common:labels.amountSelected', 'selected')}`}
              </span>
            </FilterButton>
          </Dropdown>
        </FilterColumn>

        <FilterColumn>
          <FilterLabel>{t('settings:labels.site', 'Site')}</FilterLabel>
          <Dropdown
            style={{width: '100%'}}
            overlay={
              <FilterMenu onClick={e => e.stopPropagation()}>
                <SearchInput
                  placeholder={t('common:placeholders.searchSites', 'Search sites')}
                  value={siteQuery}
                  onChange={e => setSiteQuery(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  data-cy="search-site"
                />
                <FilterMenuItem
                  selected={selectedSites.length === 0}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onChange?.({sites: [], taskGroups: [], taskNames: []})
                    setSiteQuery('')
                  }}
                >
                  <SharedCheckbox
                    id={`filter-site-all`}
                    checked={selectedSites.length === 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation()
                      onChange?.({sites: [], taskGroups: [], taskNames: []})
                      setSiteQuery('')
                    }}
                    label={t('common:labels.taskGroupFilterAll', 'All')}
                  />
                </FilterMenuItem>
                {filteredSites.map(s => (
                  <FilterMenuItem
                    key={s}
                    selected={selectedSites.includes(s)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      const next = selectedSites.includes(s)
                        ? selectedSites.filter(x => x !== s)
                        : [...selectedSites, s]
                      onChange?.({sites: next, taskGroups: [], taskNames: []})
                      setSiteQuery('')
                    }}
                  >
                    <SharedCheckbox
                      id={`filter-site-${s}`.replace(/\s+/g, '-')}
                      checked={selectedSites.includes(s)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.stopPropagation()
                        const next = selectedSites.includes(s)
                          ? selectedSites.filter(x => x !== s)
                          : [...selectedSites, s]
                        onChange?.({sites: next, taskGroups: [], taskNames: []})
                        setSiteQuery('')
                      }}
                      label={s}
                    />
                  </FilterMenuItem>
                ))}
              </FilterMenu>
            }
          >
            <FilterButton data-cy="filter-site">
              <span>
                {selectedSites.length === 0
                  ? t('common:labels.taskGroupFilterAll', 'All')
                  : selectedSites.length === 1
                  ? selectedSites[0]
                  : `${selectedSites.length} ${t('common:labels.amountSelected', 'selected')}`}
              </span>
            </FilterButton>
          </Dropdown>
        </FilterColumn>

        <FilterColumn>
          <FilterLabel>{t('tasks:labels.taskGroup', 'Task group')}</FilterLabel>
          <Dropdown
            style={{width: '100%'}}
            overlay={
              <FilterMenu onClick={e => e.stopPropagation()}>
                <SearchInput
                  placeholder={t('common:placeholders.searchGroups', 'Search groups')}
                  value={groupQuery}
                  onChange={e => setGroupQuery(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  data-cy="search-group"
                />
                <FilterMenuItem
                  selected={selectedTaskGroups.length === 0}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onChange?.({taskGroups: [], taskNames: []})
                    setGroupQuery('')
                  }}
                >
                  <SharedCheckbox
                    id={`filter-group-all`}
                    checked={selectedTaskGroups.length === 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation()
                      onChange?.({taskGroups: [], taskNames: []})
                      setGroupQuery('')
                    }}
                    label={t('common:labels.taskGroupFilterAll', 'All')}
                  />
                </FilterMenuItem>
                {filteredGroups.map(g => (
                  <FilterMenuItem
                    key={g}
                    selected={selectedTaskGroups.includes(g)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      const next = selectedTaskGroups.includes(g)
                        ? selectedTaskGroups.filter(x => x !== g)
                        : [...selectedTaskGroups, g]
                      onChange?.({taskGroups: next, taskNames: []})
                      setGroupQuery('')
                    }}
                  >
                    <SharedCheckbox
                      id={`filter-group-${g}`.replace(/\s+/g, '-')}
                      checked={selectedTaskGroups.includes(g)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.stopPropagation()
                        const next = selectedTaskGroups.includes(g)
                          ? selectedTaskGroups.filter(x => x !== g)
                          : [...selectedTaskGroups, g]
                        onChange?.({taskGroups: next, taskNames: []})
                        setGroupQuery('')
                      }}
                      label={t(...taskGroupNameArgs(g))}
                    />
                  </FilterMenuItem>
                ))}
              </FilterMenu>
            }
          >
            <FilterButton data-cy="filter-taskgroup">
              <span>
                {selectedTaskGroups.length === 0
                  ? t('common:labels.taskGroupFilterAll', 'All')
                  : selectedTaskGroups.length === 1
                  ? t(...taskGroupNameArgs(selectedTaskGroups[0]))
                  : selectedTaskGroups.length === 1
                  ? t(...taskGroupNameArgs(selectedTaskGroups[0]))
                  : `${selectedTaskGroups.length} ${t('common:labels.amountSelected', 'selected')}`}
              </span>
            </FilterButton>
          </Dropdown>
        </FilterColumn>

        <FilterColumn>
          <FilterLabel>{t('tasks:labels.taskName', 'Task name')}</FilterLabel>
          <Dropdown
            style={{width: '100%'}}
            overlay={
              <FilterMenu onClick={e => e.stopPropagation()}>
                <SearchInput
                  placeholder={t('common:placeholders.searchTasks', 'Search tasks')}
                  value={nameQuery}
                  onChange={e => setNameQuery(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  data-cy="search-task"
                />
                <FilterMenuItem
                  selected={selectedTaskNames.length === 0}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onChange?.({taskNames: []})
                    setNameQuery('')
                  }}
                >
                  <SharedCheckbox
                    id={`filter-name-all`}
                    checked={selectedTaskNames.length === 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation()
                      onChange?.({taskNames: []})
                      setNameQuery('')
                    }}
                    label={t('common:labels.taskGroupFilterAll', 'All')}
                  />
                </FilterMenuItem>
                {filteredNames.map(n => (
                  <FilterMenuItem
                    key={n}
                    selected={selectedTaskNames.includes(n)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      const next = selectedTaskNames.includes(n)
                        ? selectedTaskNames.filter(x => x !== n)
                        : [...selectedTaskNames, n]
                      onChange?.({taskNames: next})
                      setNameQuery('')
                    }}
                  >
                    <SharedCheckbox
                      id={`filter-name-${n}`.replace(/\s+/g, '-')}
                      checked={selectedTaskNames.includes(n)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.stopPropagation()
                        const next = selectedTaskNames.includes(n)
                          ? selectedTaskNames.filter(x => x !== n)
                          : [...selectedTaskNames, n]
                        onChange?.({taskNames: next})
                        setNameQuery('')
                      }}
                      label={n}
                    />
                  </FilterMenuItem>
                ))}
              </FilterMenu>
            }
          >
            <FilterButton data-cy="filter-taskname">
              <span>
                {selectedTaskNames.length === 0
                  ? t('common:labels.taskGroupFilterAll', 'All')
                  : selectedTaskNames.length === 1
                  ? selectedTaskNames[0]
                  : `${selectedTaskNames.length} ${t('common:labels.amountSelected', 'selected')}`}
              </span>
            </FilterButton>
          </Dropdown>
        </FilterColumn>
      </FiltersContainer>

      <ResetContainer>
        <ResetButton
          type="button"
          data-cy="reset-filters"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            e.preventDefault()
            setLocationQuery('')
            setSiteQuery('')
            setGroupQuery('')
            setNameQuery('')
            // clear parent filters as well
            onChange?.({locations: [], sites: [], taskGroups: [], taskNames: []})
            try {
              sessionStorage.removeItem('taskFilters')
              sessionStorage.removeItem('preserveTaskFilters')
            } catch (err) {
              console.error(err)
            }
            // notify parent to collapse all site accordions and clear recent edited id
            onCollapse?.()
          }}
          title={t('common:actions.reset', 'Reset')}
        >
          {t('common:actions.reset', 'Reset')}
        </ResetButton>
      </ResetContainer>
    </FiltersWrapper>
  )
}

export default EditTasks
